<script lang="ts" setup>
	import type { BatchUploadSteps } from '~/components/Uploads/batch.vue'
	import { useResourcesApi } from '~/services/apis/resources'

	const props = defineProps<{
		uploadSteps: BatchUploadSteps
		previewImg: string
		fileName?: string
		planId: string
	}>()
	const emits = defineEmits<{
		(event: 'done', param: unknown): void
	}>()

	const { product_id } = useRoute().params
	const { batchUploadTemplate, getCoverPeriod } = useResourcesApi()
	const { readFile, downloadFileResource } = useFileExplorer()
	const { fetchStream, uploadProgress, error, pending } = useStreamReader()
	const { CoverPeriods } = useProducts()

	const removalWarning = shallowRef(false)
	const failed = shallowRef(false)
	const downloadingTemplate = shallowRef(false)

	const activating = shallowRef(false)
	const purchaseResult = ref<RecordSchema | null>(null)

	const formData = ref<RecordSchema>({
		file: null as null | File,
		product_id: product_id as string,
		cover_period: 'annual',
		payment_channel: '',
		transaction_id: '',
		sales_type: 'new purchase'
	})

	const setFormData = (template: RecordSchema) => {
		const formSetup = new FormData()
		for (const key in template) {
			formSetup.set(key, template[key])
		}
		return formSetup
	}

	const downLoadFile = async () => {
		try {
			downloadingTemplate.value = true
			const result = await batchUploadTemplate(props.planId as string)
			if (result.data.value) {
				readFile(result.data.value, 'readAsDataURL', file => {
					const fileTarget = file?.target as FileReader
					if (fileTarget.result) {
						downloadFileResource(
							fileTarget.result as string,
							`${props.fileName || 'purchase-template'}.xlsx`
						)
					}
				})
			}
		} catch (e) {
			console.error(e)
		} finally {
			downloadingTemplate.value = false
		}
	}
	const submitFile = async () => {
		failed.value = false
		try {
			const template: RecordSchema = {
				purchase_id: props.planId as string,
				file: formData.value.file as File
			}
			const formSetup = setFormData(template)
			const result = await fetchStream<BatchPurchaseResponse>('/batch/add-employee', {
				body: formSetup,
				methods: 'POST'
			})
			if (result?.data) {
				purchaseResult.value = result.data
				useNotification().alert({
					status: result.responseCode,
					message: result.responseText
				})
				emits('done', result.data)
			}
			if (error.value) {
				failed.value = true
			}
		} catch (e) {
			console.error(e)
		}
	}
	const removeFile = (execute = false) => {
		removalWarning.value = false
		if (execute) {
			formData.value.file = null
			purchaseResult.value = null
		} else {
			removalWarning.value = true
		}
	}

	onMount(() => {
		if (!CoverPeriods.length) {
			getCoverPeriod()
		}
	})
</script>

<template>
	<div class="mx-auto w-full max-w-[800px]">
		<uploads-progress v-if="pending && !error" :file="formData.file" :progress="uploadProgress" />
		<div v-else-if="purchaseResult && !error" class="">
			<uploads-uploaded-card @remove="removeFile">
				<div class="flex w-full flex-col items-center justify-center">
					<img src="@/assets/images/png/excel-icon.png" :height="80" :width="80" alt="excel-icon" />
					<div class="mt-2 text-sm font-medium tracking-wide text-gray-500">
						{{ formData.file?.name || 'N/A' }}
					</div>
				</div>
			</uploads-uploaded-card>
		</div>

		<template v-else>
			<uploads-batch
				v-model:file="formData.file"
				:preview-img-src="previewImg"
				:downloading="downloadingTemplate"
				:step-guide="uploadSteps"
				:upload-failed="error !== null"
				:re-upload-message="error || ''"
				@change="submitFile"
				@download="downLoadFile"
			/>
		</template>
		<loader-with-text :active="activating" text="Activating Policy Holder" />

		<dialogs-error
			v-model:show="removalWarning"
			type="warning"
			header="Delete File"
			wrapper-class="bg-gray-50 w-full md:py-8 py-3 md:px-5 rounded space-y-5"
		>
			<template #icon>
				<div class="light-shadow flex h-[80px] w-[80px] items-center justify-center rounded-full border">
					<lazy-tools-icon dir="fill" icon="warning" path-class="fill-warning-400" />
				</div>
			</template>

			<template #paragraph>
				<div class="">
					You are about to remove this file -
					<span class="font-medium text-primary-2"> {{ formData.file?.name }} </span>
					<br />
					You will have to re-upload this file again
				</div>
			</template>

			<template #button="{ close }">
				<div class="flex w-full items-center justify-between gap-x-8">
					<button
						type="button"
						class="default-btn flex-1 justify-center bg-gray-200 font-medium text-gray-950"
						@click="close"
					>
						Back
					</button>
					<button
						type="button"
						class="default-btn flex-1 justify-center bg-primary-2 font-medium tracking-wide"
						@click="removeFile(true)"
					>
						Delete file
					</button>
				</div>
			</template>
		</dialogs-error>
	</div>
</template>
